/* iter-50p — Device detection (Phase 1, item 3).
 *
 * Client-side detector: any of (a) viewport < 768px wide, (b) UA hints
 * (Mobi/Android/iPhone/iPad), (c) touch-points >= 1 with no fine pointer
 * counts as "mobile". A `?desktop=1` override force-routes to the
 * desktop variant for testing or for users who prefer it; the choice
 * is remembered in localStorage so they only have to do it once.
 *
 * Used by `<Route path="/bookstore">` to swap between BookstoreLanding
 * and MobileBookstoreView. (The former MobileLandingView split was
 * retired — mobile visitors now land directly on the Bookstore.)
 */
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'cb_force_desktop';

function detect() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('desktop') === '1') {
      localStorage.setItem(STORAGE_KEY, '1');
      return false;
    }
    if (params.get('mobile') === '1') {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    }
    if (localStorage.getItem(STORAGE_KEY) === '1') return false;
  } catch (_e) { /* SSR or storage blocked */ }

  const ua = (navigator.userAgent || '').toLowerCase();
  const uaMobile = /mobi|android|iphone|ipad|ipod|blackberry|opera mini|iemobile/i.test(ua);
  const narrow = window.innerWidth <= 768;
  const touchOnly = (navigator.maxTouchPoints || 0) > 0 &&
    !window.matchMedia?.('(pointer:fine)').matches;
  return uaMobile || narrow || touchOnly;
}

function detectSmallPhone() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('desktop') === '1' || localStorage.getItem(STORAGE_KEY) === '1') return false;
    if (params.get('mobile') === '1') return true;
  } catch (_e) { /* SSR or storage blocked */ }
  const ua = (navigator.userAgent || '').toLowerCase();
  const tablet = /ipad|tablet|kindle|silk/i.test(ua)
    || (/android/i.test(ua) && !/mobile/i.test(ua))
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
  const phoneUa = /mobi|iphone|ipod|android.*mobile|blackberry|opera mini|iemobile/i.test(ua);
  const shortestSide = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight,
  );
  const compact = shortestSide < 600;
  const touchOnly = (navigator.maxTouchPoints || 0) > 0
    && !window.matchMedia?.('(pointer:fine)').matches;
  return !tablet && compact && (phoneUa || touchOnly);
}

export function useIsMobile() {
  const [mobile, setMobile] = useState(detect());
  useEffect(() => {
    const onResize = () => setMobile(detect());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export function useIsSmallPhone() {
  const [smallPhone, setSmallPhone] = useState(detectSmallPhone());
  useEffect(() => {
    const update = () => setSmallPhone(detectSmallPhone());
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  return smallPhone;
}

export function clearDesktopOverride() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* noop */ }
}
